import { useState } from 'react'

import { cn } from '@gto/ui/cn'
import { breakpointStrings } from '@gto/ui/theme/design-tokens'

/**
 * Narrow viewports lay the copy across the full slide and draw no artwork, so the
 * bytes would be paid for and never seen. A matching `<source>` is what keeps that
 * saving: the browser resolves the artwork against it and never requests the real
 * file — the same thing the `bg-[image:none]` override used to get for free from
 * CSS, which an `<img>` alone would have quietly given up.
 */
const ARTWORK_HIDDEN_MEDIA = `(max-width: ${breakpointStrings.md})`

/**
 * `<source>` has to point somewhere, and an inline pixel is the only value that
 * satisfies it without costing a request — so this is what stands in for the
 * artwork below that width.
 */
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

export type DashboardSlideArtworkProps = {
  /**
   * Assumed to be a real URL: the caller gates on the slide having artwork, which
   * is why there is no empty-string guard here. An artwork-less slide renders no
   * artwork element at all rather than an empty one.
   */
  src: string
  /**
   * Fetch ahead of the other slides' artwork. The carousel mounts every slide at
   * once, so with no order stated the browser splits bandwidth evenly across
   * pictures nobody is looking at yet, and the one on screen is the last to land.
   */
  priority?: boolean
  /**
   * A failed artwork load is otherwise permanent and completely silent — the fade
   * never runs, so the slide keeps its copy and simply never shows a picture.
   * Reported through the caller rather than from here, which is what lets this
   * library stay free of any analytics dependency, exactly as `actions` does.
   *
   * Worth wiring rather than theoretical: these URLs are presigned and expire, so
   * a dashboard left open past the hour gets a 403 on artwork that loaded fine
   * when the page opened. As a CSS background there was no event to hear it on.
   */
  onError?: () => void
}

/**
 * Which picture a URL points at, ignoring the query string.
 *
 * The two halves of the URL have different lifetimes: the path names the artwork,
 * while the query carries a presigned signature the backend re-mints on every
 * settings response. Tracking the whole URL would treat each re-signing as new
 * artwork and re-run the fade on a picture that never changed — trading a seamless
 * swap for a flicker. Tracking the path alone resets the fade only when the picture
 * genuinely differs.
 */
const artworkIdentity = (url: string): string => url.split('?')[0] ?? url

/**
 * A slide's artwork, faded in once its bytes have actually arrived.
 *
 * An `<img>` rather than the `background-image` this was until now, for two
 * reasons a background cannot offer. It fires no load event, so the artwork could
 * only ever appear abruptly — and its request cannot start until styles have been
 * computed, which on the dashboard is already several async hops behind the copy.
 */
export const DashboardSlideArtwork = ({
  src,
  priority = false,
  onError,
}: DashboardSlideArtworkProps) => {
  // Which artwork has painted, rather than a bare "has loaded": a slide can outlive
  // the picture on it — same announcement, new artwork — and a boolean left true
  // from the previous one would show the replacement instantly at full opacity,
  // before its bytes had arrived.
  const [loadedArtwork, setLoadedArtwork] = useState<string | null>(null)
  const identity = artworkIdentity(src)
  const isLoaded = loadedArtwork === identity
  const markLoaded = () => setLoadedArtwork(identity)

  return (
    // Decorative, and the carousel advances on a click anywhere in the slide. Left
    // interactive, this layer would also let the artwork be dragged out as a file.
    <picture className="pointer-events-none absolute inset-0">
      <source
        media={ARTWORK_HIDDEN_MEDIA}
        srcSet={TRANSPARENT_PIXEL}
      />
      <img
        // An artwork already in cache can be complete before React attaches
        // `onLoad`, and a load event missed that way would hold the slide at
        // opacity 0 for good — the artwork present, paid for, and never shown.
        //
        // `complete` alone cannot say that, because it is also true for a BROKEN
        // image, and this callback re-runs on every render — which the carousel does
        // on each auto-slide tick. Gated on `complete` only, a failed artwork would
        // fade ITSELF in seconds after failing, showing a broken image and undoing
        // the error path. A decode leaves intrinsic dimensions behind; a failure
        // does not, so `naturalWidth` is what distinguishes the two.
        ref={(node) => {
          if (node?.complete === true && node.naturalWidth > 0) {
            markLoaded()
          }
        }}
        alt=""
        decoding="async"
        fetchPriority={priority ? 'high' : 'low'}
        src={src}
        className={cn(
          'size-full object-cover object-right transition-opacity duration-500',
          isLoaded ? 'opacity-100' : 'opacity-0',
        )}
        onError={onError}
        onLoad={markLoaded}
      />
    </picture>
  )
}
